/**
 * Pure transform functions for Modbus field encoding/decoding
 */

import { splitHighLow, mergeHighLow } from 'ngx-modbus';

import { FieldTransformType } from '../types/config-form.types';

// ============ Time Transforms ============

/**
 * Convert register value to [hour, minute]
 * Register format: high byte = hour, low byte = minute
 */
export const registerToTime = (value: number | null | undefined): [number, number] | null => {
  if (value === null || value === undefined) return null;
  return splitHighLow(value);
};

/**
 * Convert [hour, minute] to register value
 */
export const timeToRegister = (hour: number, minute: number): number => {
  return mergeHighLow(hour, minute);
};

/**
 * Parse HH:MM string to register value
 */
const parseTimeString = (timeStr: string): number | null => {
  const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const [, h, m] = match;
  return timeToRegister(parseInt(h, 10), parseInt(m, 10));
};

// ============ Date Transforms ============

/**
 * Convert register value to [month, day]
 * Register format: high byte = month, low byte = day
 */
export const registerToDate = (value: number | null | undefined): [number, number] | null => {
  if (value === null || value === undefined) return null;
  return splitHighLow(value);
};

/**
 * Convert [month, day] to register value
 */
export const dateToRegister = (month: number, day: number): number => {
  return mergeHighLow(month, day);
};

// ============ Weekday Transforms ============

/**
 * Days of week mapping
 * Bit 0 = Sunday, Bit 1 = Monday, ... Bit 6 = Saturday
 */
export const WEEKDAY_BITS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * Convert register bitmask to array of active days
 */
export const registerToWeekdays = (value: number | null | undefined): string[] => {
  if (value === null || value === undefined) return [];
  const days: string[] = [];
  for (let i = 0; i < 7; i++) {
    if ((value >> i) & 1) {
      days.push(WEEKDAY_BITS[i]);
    }
  }
  return days;
};

/**
 * Convert array of day names to register bitmask
 */
export const weekdaysToRegister = (days: string[]): number => {
  let value = 0;
  for (const day of days) {
    const idx = WEEKDAY_BITS.indexOf(day as typeof WEEKDAY_BITS[number]);
    if (idx >= 0) {
      value |= 1 << idx;
    }
  }
  return value;
};

// ============ Bit Transforms ============

/**
 * Extract a single bit from a register value → boolean
 */
export const registerToBitToggle = (value: number | null | undefined, bitIndex: number): boolean => {
  if (value === null || value === undefined) return false;
  return ((value >> bitIndex) & 1) === 1;
};

/**
 * Set/clear a single bit in a register value → new register value
 */
export const bitToggleToRegister = (regValue: number, bitValue: boolean, bitIndex: number): number => {
  return bitValue
    ? regValue | (1 << bitIndex)
    : regValue & ~(1 << bitIndex);
};

/**
 * Extract a range of bits from a register value → number
 */
export const registerToBitSelect = (value: number | null | undefined, startBit: number, length: number): number => {
  if (value === null || value === undefined) return 0;
  const mask = (1 << length) - 1;
  return (value >> startBit) & mask;
};

/**
 * Set a range of bits in a register value → new register value
 */
export const bitSelectToRegister = (regValue: number, selection: number, startBit: number, length: number): number => {
  const mask = ((1 << length) - 1) << startBit;
  return (regValue & ~mask) | ((selection << startBit) & mask);
};

// ============ Generic Transforms ============

export interface TransformContext {
  type: FieldTransformType;
  factor?: number;
}

/**
 * Apply read transform (register → display value)
 */
export const applyReadTransform = (
  value: number | null | undefined,
  transform: TransformContext
): any => {
  if (value === null || value === undefined) return null;

  switch (transform.type) {
    case 'time':
      return registerToTime(value);
    case 'date':
      return registerToDate(value);
    case 'weekday':
      return registerToWeekdays(value);
    case 'multiply':
      return value * (transform.factor ?? 1);
    case 'divide':
      return value / (transform.factor ?? 1);
    case 'writeMultiply':
      return value;
    case 'none':
    default:
      return value;
  }
};

/**
 * Apply write transform (display value → register)
 */
export const applyWriteTransform = (
  value: any,
  transform: TransformContext
): number | null => {
  if (value === null || value === undefined) return null;

  switch (transform.type) {
    case 'time':
      if (Array.isArray(value) && value.length === 2) {
        return timeToRegister(value[0], value[1]);
      }
      if (typeof value === 'string') {
        return parseTimeString(value);
      }
      return null;
    case 'date':
      if (Array.isArray(value) && value.length === 2) {
        return dateToRegister(value[0], value[1]);
      }
      return null;
    case 'weekday':
      if (Array.isArray(value)) {
        return weekdaysToRegister(value);
      }
      return null;
    case 'multiply':
      return Math.round(value / (transform.factor ?? 1));
    case 'divide':
      return Math.round(value * (transform.factor ?? 1));
    case 'writeMultiply':
      return Math.round(value * (transform.factor ?? 1));
    case 'none':
    default: {
      if (typeof value === 'number') return value;
      const num = Number(value);
      return isNaN(num) ? value : num;
    }
  }
};
